import { useEffect, useState } from 'react';

import { ConfigProvider, useConfig } from 'shared/config/ConfigContext';
import { PopularCities } from 'shared/seo/warp/PopularCities';
import { PopularCountries } from 'shared/seo/warp/PopularCountries';
import { LinkTree } from 'shared/seo/warp/LinkTree';
import hotelData from 'shared/seo/data/hotel.json';

import { feConfig } from '@/config';
import { getCountriesForDestination, hotelCities, hotelCountries, hotelMapping } from '@/data';
import { verticals } from '@/data/verticals';
import { DestinationPage } from '@/components/DestinationPage';
import { Offers } from '@/components/offers/Offers';
import { cityLevel1Factory, hotelSearchFactory } from '@/components/factory';

export async function getStaticPaths() {
    const destinations = hotelCities.map((place) => ({
        params: { cityLevel1: place.cityPath, countryLevel1: place.countryPath },
    }));

    return {
        paths: destinations,
        fallback: false,
    };
}

export async function getStaticProps(context) {
    const destination = hotelCities.find((place) => place.cityPath === context.params.cityLevel1.toLowerCase());
    const destinationCountry = hotelCountries.find((place) => place.countryPath === context.params.countryLevel1.toLowerCase());

    const popularCities = (hotelMapping[destinationCountry.id] ?? []).filter((mapped) => mapped.city !== destination.city).slice(0, 11);
    const popularCountries = getCountriesForDestination(destinationCountry, hotelCountries);

    return {
        props: {
            destination: {
                ...destination,
                continent: destinationCountry.continent,
            },
            popularCities,
            popularCountries,
            config: feConfig,
        },
    };
}

const Inner = ({ destination, popularCities, popularCountries }) => {
    const [offers, setOffers] = useState([]);
    const [offersLoading, setOffersLoading] = useState(true);
    const { hotelOfferUrl } = useConfig();
    const offersUrl = `${hotelOfferUrl}?destination=kplace:${destination.placeKey}&rooms=2&pageSize=6&sortField=rating&sortDirection=descending`;

    useEffect(() => {
        fetch(offersUrl)
            .then((res) => (res.ok ? res.json() : Promise.reject(`Fetching offers failed (${res.status}): ${res.url}`)))
            .then((json) => {
                setOffers(json.results ?? []);
                setOffersLoading(false);
            })
            .catch(() => {
                setOffersLoading(false);
            });
    }, []);

    const factory = cityLevel1Factory(destination, hotelData, hotelSearchFactory());

    return (
        <DestinationPage factory={factory} vertical={verticals.hotel} data={hotelData}>
            <Offers
                loading={offersLoading}
                destination={destination.city}
                placeKey={destination.placeKey}
                offers={offers}
                vertical={verticals.hotel}
                heading={hotelData.lang.headingOffers}
                showMap
            />
            <PopularCities
                destination={destination}
                cities={popularCities}
                cdnUrl={hotelData.cdnPath}
                verticalUrl={hotelData.basePath}
                lang={hotelData.lang}
            />
            <PopularCountries
                destination={destination}
                countries={popularCountries}
                cdnUrl={hotelData.cdnPath}
                verticalUrl={hotelData.basePath}
                lang={hotelData.lang}
            />
            <LinkTree
                heading={hotelData.lang.linkTree.heading}
                description={hotelData.lang.linkTree.description}
                data={hotelData.linkTree}
                url={hotelData.basePath}
            />
        </DestinationPage>
    );
};

const CityLevel1 = ({ destination, popularCities, popularCountries, config }) => {
    return (
        <ConfigProvider config={config}>
            <Inner destination={destination} popularCities={popularCities} popularCountries={popularCountries} />
        </ConfigProvider>
    );
};

export default CityLevel1;
